import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../../router/all_routes";
import CollapseHeader from "../../../../../core/common/collapse-header";
import { checkPermission } from "../../../../../core/permissons/CheckPermissons";
import { Table, Button, Form } from "react-bootstrap";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartEvent, ActiveElement } from "chart.js";
import apiDefinitions from "../../../../../core/api/apiDefinitions";
import {
  showSuccessAlert,
  showSuccessResetAlert,
  showError,
} from "../../../../../core/alert/sweetAlertConfig";
import ViewUserModal from "./ViewUserModal";
import EditUserModal from "./EditUserModal";
import ResetPasswordModal from "./ResetPasswordModal";
import UserTypeModal from "./UserTypeModal";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const route = all_routes;

interface User {
  empNumber: string;
  name: string;
  createdAt: string;
  status: string;
  email?: string;
  phone?: string;
  userType?: string;
  departmentNames?: string[];
  licenseNumber?: string;
  licenseType?: string;
  expireDate?: string;
}

interface Group {
  id: number;
  name: string;
  description?: string;
  status?: string;
}

interface Department {
  id: number;
  name: string;
}

const ViewUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [showResetModal, setShowResetModal] = useState<boolean>(false);
  const [showUserTypeModal, setShowUserTypeModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [viewUser, setViewUser] = useState<User | null>(null);
  const [resetEmpId, setResetEmpId] = useState<string>("");
  const [selectedUserType, setSelectedUserType] = useState<string>("");
  const [userTypeUsers, setUserTypeUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [formData, setFormData] = useState({
    employeeNo: "",
    userType: "",
    status: "",
    departmentNames: [] as string[],
    licenseNumber: "",
    licenseType: "",
    expireDate: "",
  });
  const [departments, setDepartments] = useState<Department[]>([]);

  const navItems = [
    {
      label: "View Users",
      icon: "ti ti-layout-dashboard",
      link: route.ViewUsers,
      permission: "ROLE_GET_USERS",
    },
    {
      label: "Add Users",
      icon: "ti ti-plus",
      link: route.AddUsers,
      permission: "ROLE_ADD_USERS",
    },
    {
      label: "Add Key Approver",
      icon: "ti ti-key",
      link: route.addkeyapprover,
      permission: "ROLE_ASSIGN_APPROVER",
    },
    {
      label: "Deactivate Key Approver",
      icon: "ti ti-x",
      link: route.deactivatekeyapprover,
      permission: "ROLE_ASSIGN_APPROVER",
    },
  ];

  const statusOptions = ["ACTIVE", "BLOCKED"];
  const userTypes = [
    "HR_MANAGER",
    "DEPARTMENT_HEAD",
    "SERVICE_MANAGER",
    "USER",
    "DRIVER",
    "MINOR_STAFF",
    "SECURITY",
  ];

  // Pie chart data
  const userTypeCounts = userTypes.map((type) => ({
    type,
    count: users.filter((user) => user.userType === type).length,
  }));
  const totalUsers = users.length;
  const chartData = {
    labels: userTypeCounts.map((item) => item.type),
    datasets: [
      {
        data: userTypeCounts.map((item) =>
          totalUsers > 0 ? Number(((item.count / totalUsers) * 100).toFixed(2)) : 0
        ),
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
          "#C9CBCF",
        ],
        borderColor: [
          "#FFFFFF",
          "#FFFFFF",
          "#FFFFFF",
          "#FFFFFF",
          "#FFFFFF",
          "#FFFFFF",
          "#FFFFFF",
        ],
        borderWidth: 1,
      },
    ],
  };

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    fetchGroups();
  }, []);

  useEffect(() => {
    const filtered = users.filter((user) =>
      user.empNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    const allUsers: User[] = [];
    const invalidUsers: Partial<User>[] = [];

    try {
      const payload = {
        filterType: null,
        filterValue: "",
        date: null,
      };

      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await apiDefinitions.getUsers(page, payload);

        if (response.data.status === "S1000") {
          const usersPage = response.data.data.users;

          if (!usersPage || usersPage.length === 0) {
            hasMore = false;
            break;
          }

          const mappedUsers = usersPage
            .filter((user: any) => {
              if (!user.empNumber) {
                invalidUsers.push(user);
                return false;
              }
              return true;
            })
            .map((user: any) => {
              const userType = user.group?.replace("_K", "") || "USER";
              let expireDate = "";
              if (user.driverSpecificData?.expireDate) {
                const date = new Date(user.driverSpecificData.expireDate);
                expireDate = !isNaN(date.getTime()) ? date.toISOString().split("T")[0] : "";
              }

              return {
                empNumber: user.empNumber.toString(),
                name: user.name || "Unknown",
                createdAt: user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "N/A",
                status: user.status || "PENDING",
                email: user.email || "",
                phone: user.phone || "",
                userType,
                departmentNames: user.departmentAdminSpecificData?.departmentNames?.length
                  ? [user.departmentAdminSpecificData.departmentNames[0]]
                  : [],
                licenseNumber: user.driverSpecificData?.licenseNumber || "",
                licenseType: user.driverSpecificData?.licenseType || "",
                expireDate,
              };
            });

          allUsers.push(...mappedUsers);
          page++;
        } else {
          showError("Failed to fetch users: " + response.data.statusDescription);
          break;
        }
      }

      if (invalidUsers.length > 0) {
        showError(`${invalidUsers.length} users were excluded due to missing employee numbers.`);
      }

      setUsers(allUsers);
    } catch (error: any) {
      showError(
        "Failed to fetch users: " + (error.response?.data?.statusDescription || "Network error")
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiDefinitions.getDepartments(1, {});
      if (response.data.status === "S1000") {
        setDepartments(response.data.data.departments || []);
      } else {
        showError("Failed to fetch departments: " + response.data.statusDescription);
      }
    } catch (error: any) {
      showError(
        "Failed to fetch departments: " + (error.response?.data?.statusDescription || "Network error")
      );
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await apiDefinitions.getAllGroups();
      if (response.data.status === "S1000") {
        setGroups(response.data.data || []);
      } else {
        showError("Failed to fetch groups: " + response.data.statusDescription);
      }
    } catch (error: any) {
      showError(
        "Failed to fetch groups: " + (error.response?.data?.statusDescription || "Network error")
      );
    }
  };

  const handleUserTypeClick = async (userType: string) => {
    setSelectedUserType(userType);
    setLoading(true);
    try {
      const payload = {
        filterType: null,
        filterValue: "",
        date: null,
      };
      const response = await apiDefinitions.getUsersByType(userType, payload);
      if (response.data.status === "S1000") {
        const usersData = response.data.data.users.map((user: any) => ({
          empNumber: user.empNumber.toString(),
          name: user.name || "Unknown",
          createdAt: user.createdAt
            ? new Date(user.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "N/A",
          status: user.status || "PENDING",
          email: user.email || "",
          phone: user.phone || "",
          userType,
          departmentNames: user.departmentAdminSpecificData?.departmentNames?.length
            ? [user.departmentAdminSpecificData.departmentNames[0]]
            : [],
          licenseNumber: user.driverSpecificData?.licenseNumber || "",
          licenseType: user.driverSpecificData?.licenseType || "",
          expireDate: user.driverSpecificData?.expireDate
            ? new Date(user.driverSpecificData.expireDate).toISOString().split("T")[0]
            : "",
        }));
        setUserTypeUsers(usersData);
        setShowUserTypeModal(true);
      } else {
        showError(`Failed to fetch users for type ${userType}: ${response.data.statusDescription}`);
      }
    } catch (error: any) {
      showError(
        `Failed to fetch users for type ${userType}: ${error.response?.data?.statusDescription || "Network error"}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    const validUserType = groups.some((group) => group.name.replace("_K", "") === user.userType)
      ? user.userType
      : "";
    let expireDate = user.expireDate || "";
    if (user.expireDate) {
      const date = new Date(user.expireDate);
      expireDate = !isNaN(date.getTime()) ? date.toISOString().split("T")[0] : "";
    }
    setFormData({
      employeeNo: user.empNumber || "",
      userType: validUserType || "",
      status: user.status || "PENDING",
      departmentNames: user.departmentNames?.length ? user.departmentNames : [],
      licenseNumber: user.licenseNumber || "",
      licenseType: user.licenseType || "",
      expireDate,
    });
    setShowModal(true);
  };

  const handleViewClick = (user: User) => {
    setViewUser(user);
    setShowViewModal(true);
  };

  const handleResetPasswordClick = (empNumber: string) => {
    setResetEmpId(empNumber);
      setShowResetModal(true);

  };

  const handleResetPassword = async () => {
    setLoading(true);
    try {
      const payload = { empId: resetEmpId };
      const response = await apiDefinitions.adminResetUserPassword(payload);
      if (response.data.status === "S1000") {
        showSuccessResetAlert("Password reset successfully");
        setShowResetModal(false);
        fetchUsers();
      } else {
        showError(`Failed to reset password: ${response.data.statusDescription || "Unknown error"}`);
      }
    } catch (error: any) {
      showError(
        `Failed to reset password: ${error.response?.data?.statusDescription || "Network error"}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setResetEmpId("");
  };

  const handleCloseUserTypeModal = () => {
    setShowUserTypeModal(false);
    setSelectedUserType("");
    setUserTypeUsers([]);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map((option) => option.value);
    setFormData((prev) => ({
      ...prev,
      departmentNames: selectedOptions.length > 1 ? [selectedOptions[0]] : selectedOptions,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.userType) {
      showError("Please select a user type");
      return;
    }

    if (!formData.status) {
      showError("Please select a status");
      return;
    }

    try {
      const payload: any = {
        empNo: formData.employeeNo,
        group: `${formData.userType}_K`,
        status: formData.status.toUpperCase(),
      };

      if (formData.userType === "DEPARTMENT_HEAD" || formData.userType === "USER") {
        if (formData.departmentNames.length === 0) {
          showError("Please select a department");
          return;
        }
        payload.departmentNames = formData.departmentNames.join(",");
      } else if (formData.userType === "DRIVER") {
        if (!formData.licenseNumber || !formData.licenseType || !formData.expireDate) {
          showError("Please provide license number, license type, and expiration date for Driver");
          return;
        }
        payload.licenseNumber = formData.licenseNumber;
        payload.licenseType = formData.licenseType;
        payload.expireDate = new Date(formData.expireDate).toISOString();
      }

      setLoading(true);
      const response = await apiDefinitions.updateUser(payload);
      if (response.data.status === "S1000") {
        showSuccessAlert("User updated successfully");
        setShowModal(false);
        fetchUsers();
      } else {
        showError(`Failed to update user: ${response.data.statusDescription || "Unknown error"}`);
      }
    } catch (error: any) {
      showError(
        `Failed to update user: ${error.response?.data?.statusDescription || "Network error"}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Administration-Users</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <ul className="nav nav-tabs nav-tabs-bottom">
                    {navItems.map((item, index) =>
                      item.permission && checkPermission(item.permission) ? (
                        <li className="nav-item me-3" key={index}>
                          <Link
                            to={item.link}
                            className={`nav-link px-0 ${index === 0 ? "active" : ""}`}
                          >
                            <i className={item.icon}></i> {item.label}
                          </Link>
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="row justify-content-center">
                        {/* First Pie Chart */}
                        <div className="col-md-6 text-center mb-4">
                          <div style={{ maxWidth: "300px", margin: "0 auto" }}>
                            <Pie
                              data={chartData}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: {
                                    display: false,
                                  },
                                  title: {
                                    display: true,
                                    text: "User Type Distribution (%)",
                                  },
                                  tooltip: {
                                    callbacks: {
                                      label: (context: any) => `${context.label}: ${context.raw}%`,
                                    },
                                  },
                                },
                                onClick: (event: ChartEvent, elements: ActiveElement[]) => {
                                  if (elements.length > 0) {
                                    const index = elements[0].index;
                                    const userType = userTypes[index];
                                    handleUserTypeClick(userType);
                                  }
                                },
                              }}
                            />
                          </div>
                          <div className="mt-3 d-flex flex-wrap justify-content-center gap-2">
                            {userTypes.map((type, index) => (
                              <div key={`left-${type}`} className="d-flex align-items-center gap-1">
                                <div
                                  style={{
                                    width: "14px",
                                    height: "14px",
                                    backgroundColor: chartData.datasets[0].backgroundColor[index],
                                    borderRadius: "50%",
                                  }}
                                />
                                <span style={{ fontSize: "0.7rem" }}>{type}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="mb-3">
                        <Form.Group>
                          <Form.Label>Search by Employee ID</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter Employee ID"
                            value={searchTerm}
                            onChange={handleSearchChange}
                          />
                        </Form.Group>
                      </div>
                      <div className="table-responsive">
                        <Table className="table text-nowrap table-borderless">
                          <thead>
                            <tr>
                              <th scope="col">User Name</th>
                              <th scope="col">Employee Id</th>
                              <th scope="col">User Type</th>
                              <th scope="col">Status</th>
                              <th scope="col">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan={5}>Loading...</td>
                              </tr>
                            ) : filteredUsers.length === 0 ? (
                              <tr>
                                <td colSpan={5}>No users found</td>
                              </tr>
                            ) : (
                              filteredUsers.map((user) => (
                                <tr key={user.empNumber}>
                                  <th scope="row">{user.name}</th>
                                  <td>{user.empNumber}</td>
                                  <td>{user.userType}</td>
                                  <td>
                                    <span
                                      className={`badge bg-${
                                        user.status === "INITIAL"
                                          ? "secondary"
                                          : user.status === "PASSWORD_RESET"
                                          ? "primary"
                                          : user.status === "ACTIVE"
                                          ? "success"
                                          : "info"
                                      }`}
                                    >
                                      {user.status}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="hstack gap-2 fs-3">
                                      <button
                                        className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                                        onClick={() => handleViewClick(user)}
                                        title="View User"
                                      >
                                        <i className="feather-eye" />
                                      </button>
                                      {checkPermission("ROLE_MANAGE_USERS") && (
                                        <button
                                          className="btn-icon btn-sm btn-soft-info rounded-pill"
                                          onClick={() => handleEditClick(user)}
                                          title="Edit User"
                                        >
                                          <i className="feather-edit" />
                                        </button>
                                      )}
                                      {checkPermission("ROLE_RESET_PASSWORD") && user.status === "ACTIVE" && (
                                        <button
                                          className="btn btn-icon btn-sm btn-soft-danger rounded-pill"
                                          onClick={() => handleResetPasswordClick(user.empNumber)}
                                          title="Reset Password"
                                        >
                                          <i className="feather-lock" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ViewUserModal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setViewUser(null);
        }}
        viewUser={viewUser}
      />

      <EditUserModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSelectedUser(null);
          setFormData({
            employeeNo: "",
            userType: "",
            status: "",
            departmentNames: [],
            licenseNumber: "",
            licenseType: "",
            expireDate: "",
          });
        }}
        formData={formData}
        groups={groups}
        handleInputChange={handleInputChange}
        handleDepartmentChange={handleDepartmentChange}
        handleUpdateUser={handleUpdateUser}
        loading={loading}
      />

      <ResetPasswordModal
        show={showResetModal}
        onHide={() => {
          setShowResetModal(false);
          setResetEmpId("");
        }}
        resetEmpId={resetEmpId}
        handleResetPassword={handleResetPassword}
        loading={loading}
      />

      <UserTypeModal
        show={showUserTypeModal}
        onHide={handleCloseUserTypeModal}
        selectedUserType={selectedUserType}
        userTypeUsers={userTypeUsers}
        loading={loading}
        onView={handleViewClick}
      />
    </div>
  );
};

export default ViewUsers;